import { useRef, useState } from "react";
import { toast } from "sonner";
import { useWebSocket } from "../../hooks/useWebSocket";
import PaperSelector from "./PaperSelector";
import ChatWindow from "./ChatWindow";

const WS_CHAT_ROUTE = "chat_literature";
const FINDING_ANSWER_TEXT = "Finding Answer......";

const LiteratureChat = ({
  productName,
  literatureFiles,
  onBack,
}) => {
  const { connectWebSocket } = useWebSocket();
  const [selectedPaper, setSelectedPaper] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const pendingAssistantIdRef = useRef(null);

  const handleSendMessage = (query) => {
    if (!productName || !selectedPaper) {
      toast.error("Select a paper first.");
      return;
    }
    const fileId = selectedPaper.file_id ?? selectedPaper.id;
    const fileName = selectedPaper.filename ?? selectedPaper.file_name ?? selectedPaper.name;
    if (!fileId) {
      toast.error("File ID missing for this paper.");
      return;
    }

    const pendingId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    pendingAssistantIdRef.current = pendingId;
    setMessages((prev) => [
      ...prev,
      { role: "user", content: query },
      // pending assistant placeholder; UI renders its own loader text
      { role: "assistant", content: "", pending: true, id: pendingId },
    ]);
    setLoading(true);
    connectWebSocket(
      WS_CHAT_ROUTE,
      {
        product_name: productName,
        file_id: fileId,
        file_name : fileName,
        query,
      },
      (parsed) => {
        const answer = parsed?.answer ?? "";
        const chunks = parsed?.reranked_chunks ?? [];
        const pendingIdCurrent = pendingAssistantIdRef.current;
        setMessages((prev) => {
          // Replace the latest pending message for this request (if present)
          const idx = pendingIdCurrent
            ? prev.findIndex((m) => m?.id === pendingIdCurrent)
            : -1;
          if (idx >= 0) {
            const next = [...prev];
            next[idx] = {
              role: "assistant",
              content: answer || "",
              reranked_chunks: chunks,
              pending: false,
              id: pendingIdCurrent,
            };
            return next;
          }
          // Fallback: append
          return [
            ...prev,
            { role: "assistant", content: answer || "", reranked_chunks: chunks },
          ];
        });
      },
      () => setLoading(false),
      () => setLoading(false),
    );
  };

  const handleSelectPaper = (paper) => {
    setSelectedPaper(paper);
    // Fresh window per paper selection
    setMessages([]);
    setLoading(false);
    pendingAssistantIdRef.current = null;
  };

  const handleClearChat = () => {
    setMessages([]);
    setLoading(false);
    pendingAssistantIdRef.current = null;
  };

  return (
    <div className="flex h-full min-h-0 flex-col gap-4 overflow-hidden">
      <div className="sticky top-0 z-10 flex items-center justify-between border-b border-base-300 bg-base-100 pb-3">
        <h2 className="text-xl font-bold text-base-content">Literature Chat</h2>
        <div className="flex items-center gap-2">
          <button
            type="button"
            className="btn btn-ghost btn-sm"
            onClick={handleClearChat}
            disabled={messages.length === 0 && !loading}
          >
            Clear chat
          </button>
          <button type="button" className="btn btn-ghost btn-sm" onClick={onBack}>
            ← Back to Viewer
          </button>
        </div>
      </div>
      <div className="grid min-h-0 flex-1 grid-cols-12 gap-4 overflow-hidden">
        <div className="col-span-12 min-h-0 overflow-hidden rounded-xl border border-base-300 bg-base-100 shadow-sm md:col-span-4">
          <PaperSelector
            papers={Array.isArray(literatureFiles)
              ? literatureFiles.filter((paper) =>
                Boolean(paper?.file_name ?? paper?.filename ?? paper?.name),
              )
              : []}
            selectedPaper={selectedPaper}
            onSelect={handleSelectPaper}
          />
        </div>
        <div className="col-span-12 min-h-0 md:col-span-8">
          <ChatWindow
            messages={messages}
            onSendMessage={handleSendMessage}
            disabled={!selectedPaper}
            loading={loading}
            placeholder="Ask a question about this paper..."
          />
        </div>
      </div>
    </div>
  );
};

export default LiteratureChat;
